export const fullscreenVertex = /* glsl */ `
in vec3 position;
out vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position, 1.0);
}`;

const gravityFunctions = /* glsl */ `
uniform sampler2D positionTexture;
uniform sampler2D velocityTexture;
uniform vec3 pointMasses[8];
uniform int pointMassCount;
uniform float gravityMass;
uniform float stepScale;
uniform float cullDistance;
uniform int substepCount;
in vec2 vUv;

vec3 gravityDelta(vec3 p) {
  vec3 total = vec3(0.0);
  for (int i = 0; i < 8; i++) {
    if (i >= pointMassCount) break;
    vec3 delta = pointMasses[i] - p;
    float distanceSquared = max(dot(delta, delta), 0.0001);
    total += normalize(delta) * (gravityMass * 0.5 / distanceSquared);
  }
  return total * stepScale;
}`;

export const simulationFragment = /* glsl */ `
precision highp float;
precision highp sampler2D;
${gravityFunctions}
layout(location = 0) out vec4 outPosition;
layout(location = 1) out vec4 outVelocity;
void main() {
  vec4 state = texture(positionTexture, vUv);
  vec3 position = state.xyz;
  vec3 velocity = texture(velocityTexture, vUv).xyz;
  // Fast-forward iterations stay in GPU registers, avoiding a separate draw
  // and render-target round trip for every animation-speed step.
  for (int step = 0; step < 20; step++) {
    if (step >= substepCount) break;
    vec3 gravity = gravityDelta(position);
    velocity += gravity;
    // Corrected symplectic Euler trial: gravity is already acceleration times
    // the timestep, so position advances only from the updated velocity.
    position += velocity * stepScale;
  }
  float visible = float(length(position) <= cullDistance);
  // Pack render-time speed into the spare position alpha channel. A negative
  // value marks a culled particle, so drawing needs no velocity texture read.
  outPosition = vec4(position, visible > 0.5 ? length(velocity) : -1.0);
  outVelocity = vec4(velocity, 1.0);
}`;

export const trailHistoryFragment = /* glsl */ `
precision highp float;
precision highp sampler2D;
uniform sampler2D positionTexture;
uniform sampler2D historyTexture;
uniform float particleGrid;
uniform float tracerCount;
layout(location = 0) out vec4 outColor;
void main() {
  ivec2 cell = ivec2(gl_FragCoord.xy);
  if (cell.x == 0) {
    float fraction = (float(cell.y) + 0.5) / tracerCount;
    float particleIndex = floor(fraction * particleGrid * particleGrid);
    vec2 particleCell = vec2(mod(particleIndex, particleGrid), floor(particleIndex / particleGrid));
    // Position alpha is also used as the particle culling flag. Trails are a
    // separate diagnostic layer, so keep their history valid even when the
    // sampled particle is outside the normal particle culling distance.
    outColor = vec4(texture(positionTexture, (particleCell + 0.5) / particleGrid).xyz, 1.0);
  } else {
    outColor = texelFetch(historyTexture, cell - ivec2(1, 0), 0);
  }
}`;

export const velocitySampleFragment = /* glsl */ `
precision highp float;
precision highp sampler2D;
uniform sampler2D velocityTexture;
uniform sampler2D positionTexture;
uniform float particleGrid;
uniform float velocityRange;
uniform float distanceRange;
uniform vec3 pointMasses[8];
uniform int pointMassCount;
in vec2 vUv;
layout(location = 0) out vec4 outColor;
void main() {
  const float sampleGrid = 64.0;
  float sampleIndex = floor(gl_FragCoord.y) * sampleGrid + floor(gl_FragCoord.x);
  float particleCount = particleGrid * particleGrid;
  float particleIndex = floor((sampleIndex + 0.5) * particleCount / (sampleGrid * sampleGrid));
  vec2 particleCell = vec2(mod(particleIndex, particleGrid), floor(particleIndex / particleGrid));
  vec2 particleUv = (particleCell + 0.5) / particleGrid;
  vec3 velocity = texture(velocityTexture, particleUv).xyz;
  vec3 particlePosition = texture(positionTexture, particleUv).xyz;
  float nearestDistance = 1e20;
  for (int index = 0; index < 8; index++) {
    if (index >= pointMassCount) break;
    nearestDistance = min(nearestDistance, distance(particlePosition, pointMasses[index]));
  }
  // Match the velocity scale used by particle colour while encoding into a
  // compact byte target for a low-cost CPU readback.
  float normalizedSpeed = clamp(length(velocity) * 2.0 / max(velocityRange, 0.001), 0.0, 1.0);
  float normalizedDistance = clamp(nearestDistance / max(distanceRange, 0.001), 0.0, 1.0);
  outColor = vec4(normalizedSpeed, normalizedDistance, 0.0, 1.0);
}`;

export const trailVertex = /* glsl */ `
precision highp float;
precision highp sampler2D;
uniform sampler2D trailTexture;
uniform float validColumns;
in vec2 trailUv;
in vec2 trailOtherUv;
in float trailAge;
in float trailSegment;
out float vAge;
out float vVisible;
void main() {
  vec4 state = texture(trailTexture, trailUv);
  vAge = trailAge;
  // validColumns guarantees both endpoints have been populated. Do not reuse
  // particle culling state here: that could make every trail vanish at once.
  vVisible = trailSegment + 2.0 <= validColumns ? 1.0 : 0.0;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(state.xyz, 1.0);
}`;

export const trailFragment = /* glsl */ `
precision highp float;
in float vAge;
in float vVisible;
out vec4 outColor;
void main() {
  if (vVisible < 0.5) discard;
  vec3 young = vec3(0.35, 0.9, 1.0);
  vec3 old = vec3(0.48, 0.2, 1.0);
  // Keep single-pixel WebGL lines legible in the Quest's high-resolution view.
  float strength = pow(1.0 - vAge, 1.25) * 0.72;
  outColor = vec4(mix(young, old, vAge), strength);
}`;

export const particleVertex = /* glsl */ `
precision highp float;
precision highp sampler2D;
uniform sampler2D positionTexture;
uniform float colourScale;
uniform float pointScale;
uniform float distantBrightness;
uniform float structureScale;
in vec2 particleUv;
out vec3 vColor;
out float vVisible;
out float vBrightness;

vec3 velocityColor(float value) {
  float t = clamp(value / max(colourScale, 0.001), 0.0, 1.0);
  vec3 cool = vec3(0.03, 0.45, 1.0);
  vec3 mid = vec3(0.55, 0.18, 1.0);
  vec3 hot = vec3(1.0, 0.72, 0.18);
  return t < 0.55 ? mix(cool, mid, t / 0.55) : mix(mid, hot, (t - 0.55) / 0.45);
}

void main() {
  vec4 state = texture(positionTexture, particleUv);
  vVisible = state.a >= 0.0 ? 1.0 : 0.0;
  vColor = velocityColor(max(state.a, 0.0) * 2.0);
  vec4 mv = modelViewMatrix * vec4(state.xyz, 1.0);
  gl_Position = projectionMatrix * mv;
  float projectedSize = pointScale / max(-mv.z, 0.01);
  float scaledDistance = max(structureScale, 0.5);
  float farAmount = smoothstep(4.0 * scaledDistance, 20.0 * scaledDistance, max(-mv.z, 0.0));
  vBrightness = mix(1.0, distantBrightness, farAmount);
  gl_PointSize = state.a < 0.0 ? 0.0 : clamp(max(projectedSize, 1.0), 1.0, 18.0);
}`;

export const particleFragment = /* glsl */ `
precision highp float;
in vec3 vColor;
in float vVisible;
in float vBrightness;
out vec4 outColor;
void main() {
  if (vVisible < 0.5) discard;
  vec2 delta = gl_PointCoord - vec2(0.5);
  float radius = length(delta);
  float edgeWidth = max(fwidth(radius), 0.015);
  float coverage = 1.0 - smoothstep(0.5 - edgeWidth, 0.5, radius);
  if (coverage <= 0.0) discard;
  float glow = smoothstep(0.5, 0.02, radius);
  outColor = vec4(vColor * (0.55 + glow * 1.8) * vBrightness, glow * 0.82 * coverage);
}`;
